import { useCallback, useEffect, useState } from 'react';
import {
  getStorageProfile,
  isStorageProfileMissing,
  listStorageDocuments,
  type StorageDocument,
  type StorageProfile
} from './storage-api';

/** `no-profile` (NEX-1164): the login has no depósito account yet — `EmpresaPage` sends it to `/registro`. */
export type EmpresaDataStatus = 'loading' | 'ready' | 'error' | 'no-profile';

export interface UseEmpresaDataResult {
  profile: StorageProfile | null;
  documents: StorageDocument[];
  status: EmpresaDataStatus;
  /** Re-fetches both the profile and the document list — passed down as `onChanged` to
   *  `DocumentsCard` so an upload's effect (a new/updated row) shows up without a page reload. */
  reload: () => Promise<void>;
  /** Lets `FiscalDataForm`/`QuoteContactForm` update the displayed profile with a save's response
   *  directly, instead of a full reload for a change it already knows the result of. */
  setProfile: (profile: StorageProfile) => void;
}

/**
 * NEX-1164 — merges a `PATCH /storage/me` result into the profile on screen. That response carries
 * only the editable fields plus `approvalStatus` (`UpdateStorageProfileResultSchema` backfills
 * `rejectionReason`/`nextStep` as `null` and has no `reviewedAt`/`submittedAt`), so replacing the
 * whole profile with it would wipe the rejection reason and the next step off the banner after
 * any save. The contact pair is taken from the response only when it is there.
 */
function mergeSavedProfile(current: StorageProfile | null, saved: StorageProfile): StorageProfile {
  if (!current) return saved;
  return {
    ...current,
    razonSocial: saved.razonSocial,
    rut: saved.rut,
    email: saved.email,
    phone: saved.phone,
    approvalStatus: saved.approvalStatus,
    breakdown: { approvalStatus: saved.approvalStatus },
    contactEmail: saved.contactEmail !== undefined ? saved.contactEmail : current.contactEmail,
    contactPhone: saved.contactPhone !== undefined ? saved.contactPhone : current.contactPhone
  };
}

/** Fetches `GET /storage/me` and `GET /storage/documents` together — `EmpresaPage`'s one
 *  loading/error state covers both, since the screen has nothing useful to show with only one. */
export function useEmpresaData(): UseEmpresaDataResult {
  const [profile, setProfileState] = useState<StorageProfile | null>(null);
  const [documents, setDocuments] = useState<StorageDocument[]>([]);
  const [status, setStatus] = useState<EmpresaDataStatus>('loading');

  const reload = useCallback(async () => {
    setStatus('loading');
    // allSettled, not all: the profile's own 404 decides `no-profile`, whichever call fails first.
    const [profileResult, documentsResult] = await Promise.allSettled([getStorageProfile(), listStorageDocuments()]);
    if (profileResult.status === 'rejected' && isStorageProfileMissing(profileResult.reason)) {
      setStatus('no-profile');
      return;
    }
    if (profileResult.status === 'rejected' || documentsResult.status === 'rejected') {
      setStatus('error');
      return;
    }
    setProfileState(profileResult.value);
    setDocuments(documentsResult.value.documents);
    setStatus('ready');
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const setProfile = useCallback((saved: StorageProfile) => {
    setProfileState((current) => mergeSavedProfile(current, saved));
  }, []);

  return { profile, documents, status, reload, setProfile };
}
